import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, PhoneSchema, nullableText, requiredText } from './common.js';
import { CheckoutPaymentMethodSchema, DeliveryMethodSchema, PaymentMethodSchema, SplitPreferenceSchema, } from './enums.js';
import { MoneySchema } from './money.js';
import { OrderPaymentStateSchema, PaymentResponseSchema } from './payment.js';
import { ShipmentResponseSchema } from './shipment.js';
import { OrderStatusCodeSchema } from './statuses.js';
import { PublicStageSchema } from './enums.js';
/**
 * Buyurtma — PUL VA MIJOZ. Tovarlar unda emas, jo'natmalarda:
 *
 *   Order (pul va mijoz)
 *     └── Shipment (logistika birligi)
 *           └── OrderItem (snapshot bilan)
 */
/** Birinchi qatlam — ro'yxat. Bitta sana, progress bar YO'Q. */
export const OrderListItemSchema = z.object({
    id: IdSchema,
    number: z.string(),
    createdAt: IsoDateTimeSchema,
    grandTotal: MoneySchema,
    paymentState: OrderPaymentStateSchema,
    /** Eng sekin jo'natmaning sanasi — mijozga bitta sana ko'rsatiladi. */
    slowestEtaTo: IsoDateTimeSchema.nullable(),
    publicStage: PublicStageSchema,
    shipmentCount: z.number().int().positive(),
    itemCount: z.number().int().positive(),
    /** Sotuvchi ro'yxatdan ko'radi: yetkazish kerakmi, qayerga. */
    deliveryMethod: DeliveryMethodSchema,
    regionNameUz: z.string().nullable(),
    /** "2 ta tovar ertaga tayyor" satrini qurish uchun. */
    readySoonCount: z.number().int().nonnegative(),
});
/** Ikkinchi qatlam — buyurtma. Har jo'natma uchun sana va bir so'zli holat. */
export const OrderResponseSchema = z.object({
    id: IdSchema,
    number: z.string(),
    userId: IdSchema,
    customerName: z.string(),
    customerPhone: z.string(),
    regionId: IdSchema.nullable(),
    regionNameUz: z.string().nullable(),
    addressLine: nullableText(255),
    deliveryMethod: DeliveryMethodSchema,
    splitPreference: SplitPreferenceSchema,
    itemsTotal: MoneySchema,
    discountTotal: MoneySchema,
    grandTotal: MoneySchema,
    paymentMethod: PaymentMethodSchema,
    paymentState: OrderPaymentStateSchema,
    paidTotal: MoneySchema,
    comment: nullableText(500),
    /** Kelishilgan yetkazish sharti shu yerda — tizimda narx yo'q. */
    operatorNote: nullableText(1000),
    source: z.string(),
    createdAt: IsoDateTimeSchema,
    shipments: z.array(ShipmentResponseSchema),
    payments: z.array(PaymentResponseSchema),
});
/**
 * Buyurtma yaratish.
 *
 * Narx, summa va jo'natmalarga bo'lish SERVERDA hisoblanadi — klientdan
 * kelgan summaga hech qachon ishonilmaydi. Takroriy so'rov `Idempotency-Key`
 * sarlavhasi orqali to'siladi (avtopart-api-module skill).
 */
export const CreateOrderSchema = z
    .object({
    customerName: requiredText(120),
    customerPhone: PhoneSchema,
    regionId: IdSchema.optional(),
    addressLine: z.string().trim().max(255).optional(),
    deliveryMethod: DeliveryMethodSchema.default('COURIER'),
    /** Standart `SPLIT` — tayyorini darhol olish. */
    splitPreference: SplitPreferenceSchema.default('SPLIT'),
    paymentMethod: CheckoutPaymentMethodSchema.default('CASH'),
    comment: z.string().trim().max(500).optional(),
})
    .strict()
    .refine((v) => 
// `trim()` dan keyin bo'sh satr ham "manzil yo'q" (TASK-020-A).
v.deliveryMethod !== 'COURIER' ||
    (v.regionId !== undefined && v.addressLine !== undefined && v.addressLine.length > 0), {
    message: "Yetkazib berishda hudud va manzil ko'rsatilishi shart",
    path: ['addressLine'],
});
/** Operator buyurtmaning faqat shu maydonlarini tahrirlaydi. */
export const UpdateOrderSchema = z
    .object({
    customerName: requiredText(120).optional(),
    customerPhone: PhoneSchema.optional(),
    regionId: IdSchema.nullable().optional(),
    addressLine: z.string().trim().max(255).nullable().optional(),
    deliveryMethod: DeliveryMethodSchema.optional(),
    operatorNote: z.string().trim().max(1000).nullable().optional(),
})
    .strict();
export const ListOrdersQuerySchema = z
    .object({
    statusCode: OrderStatusCodeSchema.optional(),
    paymentState: OrderPaymentStateSchema.optional(),
    regionId: IdSchema.optional(),
    shopId: IdSchema.optional(),
    query: z.string().trim().max(120).optional(),
    createdFrom: IsoDateTimeSchema.optional(),
    createdTo: IsoDateTimeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(24),
    cursor: z.string().max(255).optional(),
})
    .strict();
/**
 * Operator izohi.
 *
 * `operatorNote` — KELISHILGAN YETKAZISH SHARTI. Tizimda yetkazish narxi
 * yo'q (docs/02-MAHSULOT.md): operator mijoz bilan telefonda kelishadi va
 * kelishuvni shu yerga yozadi. Buni yozib qo'yilmasa keyingi smena
 * operatori nima kelishilganini bilmaydi.
 *
 * `comment` — MIJOZNING izohi, u tahrirlanmaydi.
 *
 * `null` — izohni o'chirish. `undefined` bo'lishi mumkin emas: bo'sh
 * yuborish "tegmang" va "o'chiring" orasida ikkilanish qoldirardi.
 */
export const UpdateOperatorNoteSchema = z
    .object({
    operatorNote: z.string().trim().max(1000).nullable(),
})
    .strict();
/**
 * Ish navbati ta'riflari `work-queue.ts` da.
 *
 * TASK-003 da bu yerda joy-tutuvchi `WORK_QUEUE_KINDS` bor edi. U ish
 * navbati amalga oshirilganda ko'chirildi: guruh nomi yolg'iz emas, u
 * status ro'yxati, oqim va ruxsat bilan birga ma'no beradi — ular bitta
 * joyda turishi kerak.
 */
//# sourceMappingURL=order.js.map